import React from 'react'
import PropTypes from 'prop-types'
import {Box, Text, Link} from '@chakra-ui/react'
import {Link as RouteLink} from 'react-router-dom'
import {useUrlFromLink} from '../../helpers/urlHelper'

/**
 * A React component that renders additional blocks for a Product Detail Page (PDP) in a Page Designer context.
 * This component displays a title, text, and a link, which can be internal or external based on the provided URL.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.title - The title text to be displayed in the component.
 * @param {string} props.text - The descriptive text to be displayed in the component.
 * @param {string} props.url - The default URL to be used for the link if no custom URL is provided.
 * @param {Object} props.custom - An object containing custom properties for the component.
 * @param {Object} props.custom.url - An object defining the custom URL properties.
 * @param {string} props.custom.url.type - The type of the URL, which determines the link label and behavior (e.g., 'CATEGORY', 'PRODUCT', 'SEARCH', 'PAGE', 'EXTERNAL').
 * @param {string} [props.custom.url.name] - The name associated with the URL type, used as the link label if available.
 * @param {string} [props.custom.url.target] - The target URL or identifier, used as the link label if no name is provided.
 *
 * @returns {JSX.Element} A JSX element containing the structured block with a title, text, and a link.
 */
export const PDCAdditionalBlocksPDP = ({title, text, url, custom}) => {
    const updatedUrl = useUrlFromLink(custom?.url) || url

    const currentID = custom?.url?.target || null

    // Determine the link label based on the URL type
    const linkLabel = (() => {
        switch (custom?.url?.type) {
            case 'CATEGORY':
                return custom?.url?.name || currentID
            case 'PRODUCT':
                return custom?.url?.name || currentID
            case 'SEARCH':
                return custom?.url?.target || url
            case 'PAGE':
                return custom?.url?.name || currentID
            default:
                return updatedUrl || url
        }
    })()

    // Determine if the link is external
    const isExternal = custom?.url?.type === 'EXTERNAL' && !updatedUrl?.startsWith('/')

    return (
        <Box width="364px" mt={{base: 54, md: 'initial'}}>
            <Text variant="eyeBrowLarge" textTransform="uppercase" mb={24}>
                {title}
            </Text>
            <Text variant="bodyBase2" color="darkGray" mb={20}>
                {text}
            </Text>
            <Link as={!isExternal ? RouteLink : undefined} to={updatedUrl} href={updatedUrl}>
                {linkLabel || url}
            </Link>
        </Box>
    )
}

PDCAdditionalBlocksPDP.propTypes = {
    title: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    custom: PropTypes.shape({
        url: PropTypes.shape({
            type: PropTypes.string,
            target: PropTypes.string,
            name: PropTypes.string
        })
    })
}
